import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gem, ShieldCheck, Zap, CheckCircle2, Timer, CreditCard, HeadphonesIcon } from "lucide-react";
import { fetchPackages, validatePlayer, createCheckout } from "../lib/api";
import { useAuth } from "../lib/auth";
import PackageCard from "../components/PackageCard";
import Reviews from "../components/Reviews";
import WeeklyRanking from "../components/WeeklyRanking";
import PaymentQRDialog from "../components/PaymentQRDialog";
import ChytaPayDialog from "../components/ChytaPayDialog";
import CategoryTabs from "../components/CategoryTabs";
import { fmtARS } from "../lib/utils-app";

const HERO_IMG = "/diamonds-bg.jpeg";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [playerId, setPlayerId] = useState("");
  const [validating, setValidating] = useState(false);
  const [valid, setValid] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [qrOrder, setQrOrder] = useState(null);
  const [payOrder, setPayOrder] = useState(null);

  useEffect(() => {
    fetchPackages()
      .then((d) => setPackages(d.packages))
      .catch(() => toast.error("No se pudieron cargar los paquetes"));
  }, []);

  const canCheckout = useMemo(
    () => !!selected && !!playerId && valid?.valid === true && !processing,
    [selected, playerId, valid, processing]
  );

  async function handleValidate() {
    if (!playerId) return;
    setValidating(true);
    try {
      const res = await validatePlayer(playerId);
      setValid(res);
      if (res.valid) toast.success(res.nickname ? `Jugador: ${res.nickname}` : "ID válido");
      else toast.error(res.error || "ID inválido");
    } catch {
      toast.error("Error validando ID");
    } finally {
      setValidating(false);
    }
  }

  async function handleCheckout() {
    if (!user) {
      toast.info("Iniciá sesión con Google para continuar");
      startGoogleLogin();
      return;
    }
    if (!canCheckout) return;
    setProcessing(true);
    try {
      const res = await createCheckout({
        package_id: selected.id,
        player_id: playerId,
        player_nickname: valid?.nickname || null,
      });
      if (res.provider === "chytapay") {
        setPayOrder(res);
      } else if (res.checkout_url) {
        // Build order object client-side to avoid an extra network call before opening the dialog
        setQrOrder({
          order_id: res.order_id,
          checkout_url: res.checkout_url,
          amount_ars: selected.price_ars,
          diamonds: selected.diamonds,
          player_id: playerId,
          player_nickname: valid?.nickname || null,
          status: "pending_payment",
        });
      } else {
        toast.error("No se pudo iniciar el pago");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error creando el pago";
      toast.error(String(msg));
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div className="min-h-screen">
      <PaymentQRDialog
        open={!!qrOrder}
        onClose={() => setQrOrder(null)}
        order={qrOrder}
      />
      <ChytaPayDialog order={payOrder} onClose={() => setPayOrder(null)} />

      <CategoryTabs active="diamonds" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/75 to-black" aria-hidden />
        {/* Lightning accent */}
        <div className="absolute -top-24 right-0 sm:-right-24 w-64 h-64 sm:w-[420px] sm:h-[420px] rounded-full bg-[#ff4500]/20 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute bottom-0 left-0 w-56 h-56 sm:w-[300px] sm:h-[300px] rounded-full bg-[#ff8a00]/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10 sm:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ff4500]/10 border border-[#ff4500]/40 text-[#ff4500] text-xs tracking-widest mb-5">
              <Timer className="w-3.5 h-3.5" /> ENTREGA EN MINUTOS · 24/7
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-wider">
              RECARGAS <span className="text-[#ff4500]">al Instante</span>
              <br />
              <span className="text-white/90">DIAMANTES FREE FIRE</span>
            </h1>
            <p className="mt-5 text-neutral-300 text-base sm:text-lg max-w-2xl">
              Pagá por transferencia y recibí tus diamantes automáticamente en tu cuenta.
              Solo necesitás tu <span className="text-[#ff4500] font-semibold">ID de jugador</span>.
            </p>

            <div className="mt-6 flex flex-wrap gap-3 text-sm">
              <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-md">
                <ShieldCheck className="w-4 h-4 text-[#ff4500]" /> Pago 100% seguro
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-md">
                <Zap className="w-4 h-4 text-[#ff4500]" /> Recarga automática
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-md">
                <HeadphonesIcon className="w-4 h-4 text-[#ff4500]" /> Soporte WhatsApp
              </div>
            </div>

            <Link
              to="/recompensas"
              data-testid="hero-rewards-cta"
              className="mt-6 inline-flex items-center gap-3 rounded-xl border border-amber-400/50 bg-gradient-to-r from-amber-500/15 to-[#ff4500]/10 px-4 py-3 hover:from-amber-500/25 hover:to-[#ff4500]/20"
              style={{ transition: "background 200ms ease" }}
            >
              <span className="text-2xl">🎁</span>
              <div className="text-left">
                <div className="font-display text-lg tracking-wider text-amber-300 leading-tight">GANÁ CAJAS SORPRESA</div>
                <div className="text-xs text-neutral-300">Sumá puntos con cada recarga y canjeálos por premios</div>
              </div>
              <span className="ml-1 text-amber-300">→</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Step 1 - Player ID */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="text-xs tracking-[0.3em] text-[#ff4500]">PASO 1</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-wider mb-4">Ingresá tu ID de jugador</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          <div>
            <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
              ID de Free Fire (numérico)
            </label>
            <div className="flex gap-2">
              <input
                data-testid="player-id-input"
                type="text"
                inputMode="numeric"
                value={playerId}
                onChange={(e) => {
                  setPlayerId(e.target.value.replace(/\D/g, "").slice(0, 15));
                  setValid(null);
                }}
                placeholder="Ej: 123456789"
                className="input-field flex-1 min-w-0 px-4 py-3 rounded-md text-base tracking-wider"
              />
              <button
                data-testid="validate-id-btn"
                onClick={handleValidate}
                disabled={!playerId || validating}
                className="btn-primary px-5 py-3 rounded-md text-sm"
              >
                {validating ? "Validando..." : "Validar"}
              </button>
            </div>
            {valid && (
              <div className={`mt-2 text-sm flex items-center gap-1.5 ${valid.valid ? "text-emerald-400" : "text-rose-400"}`}>
                {valid.valid ? <CheckCircle2 className="w-4 h-4" /> : null}
                {valid.valid ? `ID válido${valid.nickname ? ` · ${valid.nickname}` : ""}, listo para recargar` : valid.error}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-white/5" />
      </div>

      {/* Step 2 - Packages */}
      <section id="paquetes" className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] text-[#ff4500]">PASO 2</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-wider">Elegí tu paquete</h2>
          </div>
          <div className="hidden sm:block text-sm text-neutral-400">
            {packages.length} paquetes disponibles
          </div>
        </div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
          className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
        >
          {packages.map((p) => (
            <motion.div
              key={p.id}
              variants={{
                hidden: { opacity: 0, y: 16 },
                show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
              }}
            >
              <PackageCard pkg={p} selected={selected?.id === p.id} onSelect={setSelected} />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-white/5" />
      </div>

      {/* Checkout summary */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24">
        <div className="text-xs tracking-[0.3em] text-[#ff4500]">PASO 3</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-wider mb-4">Confirmá y pagá</h2>

        <div className="rounded-xl border border-[#ff4500]/30 bg-[#0d0d0d] p-5 sm:p-6 max-w-3xl">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-md bg-[#0f0f0f] border border-[#ff4500]/40 grid place-items-center diamond-glow">
                <Gem className="w-6 h-6 text-[#ff4500]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Paquete</div>
                <div className="font-display text-2xl tracking-wider">
                  {selected ? `${selected.diamonds.toLocaleString("es-AR")} DIAMANTES` : "—"}
                </div>
              </div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-white/10" />
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Player ID</div>
              <div className="font-mono text-lg text-white">{playerId || "—"}</div>
              {valid?.nickname && <div className="text-xs text-emerald-400" data-testid="summary-nickname">{valid.nickname}</div>}
            </div>
            <div className="hidden sm:block h-10 w-px bg-white/10" />
            <div className="sm:ml-auto">
              <div className="text-xs uppercase tracking-widest text-neutral-500">Total</div>
              <div className="font-display text-3xl tracking-wider text-[#ff4500]">
                {selected ? fmtARS(selected.price_ars) : "—"}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <button
              data-testid="checkout-btn"
              onClick={handleCheckout}
              disabled={!canCheckout}
              className="btn-primary flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-md text-base"
            >
              <CreditCard className="w-5 h-5" />
              {user ? (processing ? "Generando cobro..." : "Pagar por transferencia") : "Iniciar sesión y pagar"}
            </button>
            <div className="text-xs text-neutral-400 sm:max-w-[220px]">
              Vas a ver un <span className="text-white">CVU</span> y el monto exacto.
              Transferí desde cualquier banco o billetera y se acredita solo.
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-t border-white/5 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Pago por transferencia", desc: "CVU único por pedido con ChytaPay. Se acredita automáticamente." },
            { icon: Zap, title: "Entrega rápida", desc: "Confirmado el pago, ejecutamos la recarga en minutos." },
            { icon: HeadphonesIcon, title: "Soporte humano", desc: "Escribinos por WhatsApp: +54 3821 452181" },
          ].map((f, i) => (
            <div key={i} className="rounded-lg border border-white/5 bg-[#0f0f0f] p-5">
              <f.icon className="w-6 h-6 text-[#ff4500] mb-3" />
              <div className="font-semibold text-white">{f.title}</div>
              <div className="text-sm text-neutral-400 mt-1">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <div className="border-t border-white/5">
        <Reviews />
      </div>

      <div className="border-t border-white/5">
        <WeeklyRanking />
      </div>
    </div>
  );
}
